

import { useMemo } from "react";

const SCORE_CONFIG = {
    excellent: {
        text: "Excellent",
        color: "#0c9012",
        bgColor: "#a2e884",
        defaultMin: 95,
        defaultMax: 100,
    },
    good: {
        text: "Good",
        color: "#3bbd2E",
        bgColor: "#DDFAB7",
        defaultMin: 85,
        defaultMax: 94,
    },
    average: {
        text: "Average",
        color: "#ccc129",
        bgColor: "#fcf8ad",
        defaultMin: 75,
        defaultMax: 84,
    },
    poor: {
        text: "Poor",
        color: "#d00c0c",
        bgColor: "#ffe0cc",
        defaultMin: 0,
        defaultMax: 74,
    },
};

const parseRange = (range) => {
    if (!range || typeof range !== "string") return null;

    const values = range
        .split("to")
        .map((value) => Number(value.trim()));

    if (values.length !== 2 || values.some(Number.isNaN)) {
        return null;
    }

    return {
        min: values[0],
        max: values[1],
    };
};

// Pure function version — safe to call anywhere (loops, maps, event handlers),
// NOT just at the top level of a component like a hook would require.
export const getScoreConfig = (score = 0, colorScale) => {
    const numericScore = Number(score) || 0;

    if (colorScale) {
        for (const [key, config] of Object.entries(SCORE_CONFIG)) {
            const range = colorScale?.[key];
            if (!range) continue;
            const parsedRange = parseRange(range);
            if (!parsedRange) continue;
            const { min, max } = parsedRange;

            if (numericScore >= min && numericScore <= max) {
                return {
                    key,
                    text: config.text,
                    color: config.color,
                    bgColor: config.bgColor,
                    min,
                    max,
                };
            }
        }
    }

    for (const [key, config] of Object.entries(SCORE_CONFIG)) {
        if (
            numericScore >= config.defaultMin &&
            numericScore <= config.defaultMax
        ) {
            return {
                key,
                text: config.text,
                color: config.color,
                bgColor: config.bgColor,
                min: config.defaultMin,
                max: config.defaultMax,
            };
        }
    }

    return {
        key: "poor",
        text: SCORE_CONFIG.poor.text,
        color: SCORE_CONFIG.poor.color,
        bgColor: SCORE_CONFIG.poor.bgColor,
        min: 0,
        max: 74,
    };
};

const useScoreScale = (score = 0, colorScale) => {
    const scoreConfig = useMemo(
        () => getScoreConfig(score, colorScale),
        [score, colorScale]
    );

    const scoreLegend = useMemo(() => {
        const hasColorScale =
            colorScale &&
            typeof colorScale === "object" &&
            Object.keys(colorScale).length > 0;

        return Object.entries(SCORE_CONFIG)
            .filter(([key]) => {
                if (hasColorScale) {
                    return !!colorScale?.[key];
                }
                return true;
            })
            .map(([key, config]) => {
                const dynamicRange = colorScale?.[key];

                if (hasColorScale && dynamicRange) {
                    const parsedRange = parseRange(dynamicRange);

                    if (parsedRange) {
                        return {
                            key,
                            text: config.text,
                            min: parsedRange.min,
                            max: parsedRange.max,
                            color: config.color,
                            bgColor: config.bgColor,
                        };
                    }
                }

                return {
                    key,
                    text: config.text,
                    min: config.defaultMin,
                    max: config.defaultMax,
                    color: config.color,
                    bgColor: config.bgColor,
                };
            });
    }, [colorScale]);

    return {
        ...scoreConfig,
        score: Number(score) || 0,
        color: scoreConfig.color,
        bgColor: scoreConfig.bgColor,
        scoreLegend,
    };
};

export default useScoreScale;